import cx from "classnames";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Icon } from "../ui/icon";
import global from "../../scss/dhp.scss";
import useAutoPlay from "../../hooks/useAutoPlay";
import { formatTime } from "../../_helpers/utils";

let style = Object.assign({}, global);

const VideoPlayer = ({ templateInfo, type = "video/mp4" }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showVolumeRange, setShowVolumeRange] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showCcntroller, setShowController] = useState(false);
  const [templateType, setTemplateType] = useState();

  const isAutoPlaying = useAutoPlay(videoRef); // Use the custom hook

  const togglePlayPause = () => {
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = e => {
    const newVolume = e.target.value;
    setVolume(newVolume);
    videoRef.current.volume = newVolume;
    setIsMuted(newVolume === "0");
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    videoRef.current.muted = !isMuted;
  };

  const handleTimeUpdate = () => {
    setCurrentTime(videoRef.current.currentTime);
  };

  const handleSeek = e => {
    const newTime = e.target.value;
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleLoadedMetadata = () => {
    setDuration(videoRef.current.duration);
    setIsLoaded(true); // Set controls visibility once the video is loaded
  };

  // Set Auto Play local control
  useEffect(() => {
    setIsPlaying(isAutoPlaying);
  }, [isAutoPlaying]);

  // Set video pause after play completed
  useLayoutEffect(() => {
    if (currentTime === duration) setIsPlaying(false);
  }, [currentTime]);

  useEffect(() => {
    if (templateInfo?.type) {
      let tempType = templateInfo.type
        .toLowerCase()
        .replace(/twitter/gi, "X/Twitter")
        .replace(/youtube/gi, "YouTube")
        .replace(/\b\w/g, match => match.toUpperCase());
      setTemplateType(tempType);
    }
  }, [templateInfo]);

  return (
    <div className={style["w-100"]}>
      <figure className={style["template-image"]}>
        <div
          className={style["custom-video-player"]}
          onMouseEnter={() => setShowController(true)}
          onMouseLeave={() => setShowController(false)}>
          <video
            ref={videoRef}
            poster={templateInfo.thumb}
            src={templateInfo.preview}
            type={type}
            onTimeUpdate={handleTimeUpdate}
            onLoadedMetadata={handleLoadedMetadata}
          />

          {isLoaded && showCcntroller && (
            <div className={cx(style["controls"], style["d-flex"], style["flex-column"])}>
              <input type="range" min="0" max={Math.floor(duration)} value={currentTime} onChange={handleSeek} />
              <div
                className={cx(style["d-flex"], style["player-info"], style["my-2"])}
                onMouseLeave={() => setShowVolumeRange(false)}>
                <ul className={cx(style["d-flex"], style["player-info-left"])}>
                  <li>
                    <span
                      className={cx(style["custom-tooltip"], style["rounded"], style["m-0"])}
                      onClick={togglePlayPause}>
                      <Icon icon={`ui-${isPlaying ? "pause" : "play"}`} />
                      <div className={cx(style["custom-tooltip-content"], style["top"])}>
                        {isPlaying ? "Pause" : "Play"}
                      </div>
                    </span>
                  </li>
                  <li onMouseEnter={() => setShowVolumeRange(true)}>
                    <span className={cx(style["custom-tooltip"], style["rounded"], style["m-0"])} onClick={toggleMute}>
                      <Icon icon={`ui-${isMuted ? "volume-mute" : "volume"}`} />
                      <div className={cx(style["custom-tooltip-content"], style["top"])}>
                        {isMuted ? "Unmute" : "Mute"}
                      </div>
                    </span>
                  </li>

                  {showVolumeRange && (
                    <li>
                      <input type="range" min="0" max="1" step="0.1" value={volume} onChange={handleVolumeChange} />
                    </li>
                  )}
                </ul>

                <ul className={cx(style["player-info-right"])}>
                  <li className={style["d-flex"]}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </figure>

      <div className={style["template-info"]}>
        <p className={cx(style["fw-6"], style["m-0"])}>{templateInfo?.category}</p>
        <p className={cx(style["mb-3"], style["text-capitalize"])}>{templateType} Template</p>
        <span>
          Customize this template now and share it with the world to <br /> get more likes, followers, and hearts.
        </span>
      </div>
    </div>
  );
};

export default VideoPlayer;
